import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import take from 'lodash/take';
import uniqueId from 'lodash/uniqueId';
import { Dropdown, MenuItem } from '@cancercentrum/rcc-bootstrap';
import { useI18n } from '../../i18n';
import { isBetweenDates } from '../../utils/date';
import { filterBySearch } from './filter';
import { CustomMenu } from './CustomMenu';
import { ToggleButton } from './ToggleButton';
const DEFAULT_DEBOUNCE = 300;
const DEFAULT_ITEMS_PER_PAGE = 15;
const defaultFormatter = (value)=>{
    return value?.text || '';
};
export function DropdownList(props) {
    const { disabled = false, emptyText, formatter = defaultFormatter, onFilter, formatterText, onRootClick, optionsCaption, regvarMetadata, readOnly, onBlur, value, searchable = true, debounce: debounce2 = DEFAULT_DEBOUNCE, itemsPerPage = DEFAULT_ITEMS_PER_PAGE, drop = 'down', flip = true } = props;
    const { t } = useI18n();
    const onFilterRef = useRef(onFilter);
    onFilterRef.current = onFilter;
    const [filter, setFilter] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [visibleCount, setVisibleCount] = useState(itemsPerPage);
    const [selectedIndex, setSelectedIndex] = useState();
    // eslint-disable-next-line
    const id = useMemo(()=>props.id || uniqueId('DropdownList_'), []);
    const captionText = optionsCaption || t('regvar.list.caption');
    const emptyMessage = emptyText || /*#__PURE__*/ _jsx("em", {
        children: t('component.dropdownlist.empty')
    });
    const filterInputRef = useRef(null);
    const dropdownButtonRef = useRef(null);
    const txtFormatter = formatterText || formatter;
    const prevIsOpen = useRef(isOpen);
    const now = useMemo(()=>{
        const n = new Date();
        return new Date(n.getFullYear(), n.getMonth(), n.getDate());
    }, []);
    const getInitialOptions = ()=>{
        return props.options || regvarMetadata.listValues;
    };
    const allOptions = getInitialOptions();
    // Om props inte skickats in antas variablen vara en värdelista och värdena tas då från metadatat.
    // Options antas också vara listItems om datatypen är 'list'.
    const isOptionsListItems = !props.options || regvarMetadata?.dataType === 'list';
    const filteredOptions = useMemo(()=>{
        let opts = allOptions;
        // Om props inte skickats in antas variablen vara en värdelista och värdena tas då från metadatat.
        if (isOptionsListItems) {
            opts = opts.filter((x)=>x.id === value || isBetweenDates(now, x.validFrom, x.validTo));
        }
        const res = (onFilterRef.current || filterBySearch)(opts, filter, txtFormatter);
        return res;
    }, [
        allOptions,
        filter,
        txtFormatter,
        now,
        value,
        isOptionsListItems
    ]);
    const visibleOptions = take(filteredOptions, visibleCount);
    useEffect(()=>{
        let newSelectedIndex;
        if (filteredOptions.length === 1) {
            newSelectedIndex = 0;
        } else {
            newSelectedIndex = take(filteredOptions, itemsPerPage).findIndex((x)=>x.id === value);
        }
        if (newSelectedIndex === -1) {
            newSelectedIndex = undefined;
        }
        setSelectedIndex(newSelectedIndex);
        setVisibleCount(itemsPerPage);
    }, [
        filteredOptions,
        itemsPerPage,
        value
    ]);
    const selectedItem = useMemo(()=>{
        return allOptions.find((x)=>x.id === value);
    }, [
        allOptions,
        value
    ]);
    const buttonText = selectedItem ? formatter(selectedItem, '') : captionText;
    useEffect(()=>{
        // Då komponenten stängs ska knappen få fokus.
        if (!isOpen && prevIsOpen.current) {
            dropdownButtonRef.current?.focus();
        }
    }, [
        isOpen
    ]);
    useEffect(()=>{
        prevIsOpen.current = isOpen;
    });
    const onFilterKeyPress = (e)=>{
        switch(e.key){
            case 'ArrowDown':
                {
                    const newIndex = selectedIndex !== undefined ? selectedIndex + 1 : 0;
                    if (newIndex < visibleOptions.length) {
                        setSelectedIndex(newIndex);
                    } else if (newIndex < filteredOptions.length) {
                        setSelectedIndex(newIndex);
                        setVisibleCount(visibleCount + itemsPerPage);
                    }
                    e.preventDefault();
                    break;
                }
            case 'ArrowUp':
                {
                    const newIndex = selectedIndex !== undefined ? selectedIndex - 1 : visibleOptions.length - 1;
                    if (newIndex >= 0) {
                        setSelectedIndex(newIndex);
                    }
                    e.preventDefault();
                    break;
                }
            case 'Enter':
                {
                    if (selectedIndex !== undefined) {
                        const item = visibleOptions[selectedIndex];
                        triggerOnChange(item);
                        setIsOpen(false);
                        setSelectedIndex(undefined);
                    }
                    e.preventDefault();
                    break;
                }
            case 'Escape':
                if (!filter) {
                    setIsOpen(false);
                    setSelectedIndex(undefined);
                }
                break;
        }
    };
    if (readOnly) {
        return /*#__PURE__*/ _jsx("div", {
            className: "form-control-plaintext",
            children: buttonText
        });
    }
    const onKeyDown = (e)=>{
        if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !isOpen) {
            e.preventDefault();
            toggleOpen(true);
        }
    };
    const showMore = ()=>{
        setVisibleCount((x)=>x + itemsPerPage);
        filterInputRef.current?.focus();
    };
    const toggleOpen = (newState)=>{
        let shouldOpen = newState;
        let newSelectedIndex;
        if (props.disabled) {
            shouldOpen = false;
        }
        if (shouldOpen) {
            newSelectedIndex = filteredOptions.findIndex((x)=>x.id === value);
            if (newSelectedIndex === -1) {
                newSelectedIndex = undefined;
            }
            if (filteredOptions.length === 1) {
                newSelectedIndex = 0;
            }
        }
        setIsOpen(shouldOpen);
        setSelectedIndex(newSelectedIndex);
    };
    const triggerOnChange = (item)=>{
        props.onChange(item?.id || null, item);
    };
    const onSelect = (key)=>{
        if (typeof key !== 'string') {
            return;
        }
        // @ts-ignore eftersom DropdownList kan används för både strängvariabler och list/vd kan 'id' vara av olika datatyper
        // eslint-disable-next-line eqeqeq
        const item = allOptions.find((x)=>x.id == key) || null;
        triggerOnChange(item);
        setIsOpen(false);
        setSelectedIndex(undefined);
        // Reset if no item selecte, otherwise preserve so the selected item is highlighted when the dropdown list is expanded.
        if (!item) {
            setVisibleCount(itemsPerPage);
        }
    };
    const onFilterChange = (filterText)=>{
        if (filterText === filter) {
            return;
        }
        setFilter(filterText);
    };
    let dropdownItems = visibleOptions.map((option, i)=>{
        const text = formatter(option, filter);
        let isActive = selectedIndex === i;
        if (!isActive && selectedIndex === undefined) {
            isActive = value === option.id;
        }
        return /*#__PURE__*/ _jsx(MenuItem, {
            eventKey: option.id.toString(),
            active: isActive,
            onSelect: onSelect,
            children: text
        }, option.id);
    });
    // Om något värde är valt, lägg till "Ta bort"-alternativet.
    if (selectedItem) {
        dropdownItems.unshift(/*#__PURE__*/ _jsx(MenuItem, {
            divider: true
        }, "__RESET_DIVIDER1"));
        dropdownItems.unshift(/*#__PURE__*/ _jsx(MenuItem, {
            eventKey: "",
            onSelect: onSelect,
            children: captionText
        }, "__RESET"));
        if (searchable) {
            dropdownItems.unshift(/*#__PURE__*/ _jsx(MenuItem, {
                divider: true
            }, "__RESET_DIVIDER2"));
        }
    }
    // Om inte alla värden är synliga, lägg till "Visa fler"-alternativet.
    if (filteredOptions.length > visibleOptions.length) {
        dropdownItems.push(/*#__PURE__*/ _jsx(MenuItem, {
            divider: true
        }, "__SHOW_MORE_DIVIDER"));
        dropdownItems.push(/*#__PURE__*/ _jsx(MenuItem, {
            eventKey: "show_more",
            onClick: showMore,
            children: t('component.dropdownlist.more')
        }, "__SHOW_MORE"));
    }
    // Om inga träffar finns
    if (!visibleOptions.length && filter) {
        dropdownItems = /*#__PURE__*/ _jsx(MenuItem, {
            className: "disabled",
            children: emptyMessage
        });
    }
    return /*#__PURE__*/ _jsxs(Dropdown, {
        className: "dropdown-list",
        show: isOpen,
        onToggle: (nextOpenState, _, metadata)=>{
            if (metadata.source === 'rootClose' && onRootClick) {
                onRootClick();
            }
            if (metadata.source === 'select' && metadata.key === 'show_more' || metadata.source === 'keydown') {
                return;
            }
            toggleOpen(nextOpenState);
        },
        drop: drop,
        children: [
            /*#__PURE__*/ _jsx(Dropdown.Toggle, {
                componentClass: ToggleButton,
                ref: dropdownButtonRef,
                disabled: disabled,
                open: isOpen,
                onBlur: onBlur,
                id: id,
                onKeyDown: onKeyDown,
                children: buttonText
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "dropdown-items open",
                children: isOpen && /*#__PURE__*/ _jsx(Dropdown.Menu, {
                    componentClass: CustomMenu,
                    searchable: searchable,
                    onFilterChange: onFilterChange,
                    setInputElement: (el)=>{
                        filterInputRef.current = el;
                    },
                    defaultFilter: filter,
                    filterDebounce: debounce2,
                    onFilterKeyDown: onFilterKeyPress,
                    flip: flip,
                    children: dropdownItems
                })
            })
        ]
    });
}
